import type { Cascadify } from './util'
import type { Status } from './common'
import type { User } from './user'
import type {
  Activity,
  RelationType,
  ActivityRelationSettings,
} from './activity'
import type { ActivityGroup } from './activity-group'
import type { RoomMedia, CascadeRoomMedia } from './room-media'

export enum RoomCategory {
  MASTER = 'MASTER',
  CUSTOM = 'CUSTOM',
}

export enum EntryPage {
  Pickteams = 'pickteams',
  Game = 'game',
}

export type RoomSettingsStyles = {
  '--primary-accent-color'?: string
  '--secondary-accent-color'?: string
  '--tertiary-accent-color'?: string
  '--correct-color'?: string
  '--wrong-color'?: string
  '--primary-background-color'?: string
  '--secondary-background-color'?: string
}

export type RoomSettings = {
  externalName?: string
  duration?: number
  round?: number
  pickTeamText?: string
  players?: number
  description?: string
  shortDescription?: string
  entryPage?: EntryPage
  locked?: boolean
  hostless2?: boolean
  diy?: boolean
  moderated?: boolean
  'game-chat'?: boolean
  'team-chat'?: boolean
  tipsDisabled?: boolean
  styles?: RoomSettingsStyles
  videoDisabled?: boolean
}

export type RootRoomActivity = {
  id: number
  roomId: number
  activityId: number
  position: number
}

export type RoomActivity = RootRoomActivity & {
  activity: Activity
}

export type RoomActivityId = RoomActivity['id']

export type RootRoomActivityGroup = {
  id: number
  roomId: number
  activityGroupId: number
  position: number
}

export type RoomActivityGroup = RootRoomActivityGroup & {
  activityGroup: ActivityGroup
}

export type RoomActivityGroupId = RoomActivityGroup['id']

export type WeveGameId = string
export type WeveSessionId = string
export type WeveSessionURL = string

export type RootRoom = {
  id: number
  name: string
  sessionId?: WeveSessionId | null
  gameId?: WeveGameId | null
  url?: WeveSessionURL | null
  status: Status
  category: RoomCategory
  settings: RoomSettings
  tags: string[]
  version?: number
  createdAt: string
  updatedAt: string
  updatedBy?: User
  workspaceId?: number
}

export type Room = RootRoom & {
  media: RoomMedia[]
  relations: RoomRelation[]
  activities: RoomActivity[]
  activityGroups: RoomActivityGroup[]
}

export type CascadeRoom = Cascadify<RootRoom> & {
  media: CascadeRoomMedia[]
}

export type RoomRelationSide =
  | { type: 'Activity'; id: number }
  | { type: 'Group'; id: number }

export type RoomRelation = {
  id: number
  roomId: RoomId
  left: RoomRelationSide
  right: RoomRelationSide
  type: RelationType
  settings: ActivityRelationSettings
}

export type RoomId = Room['id']
export type RoomRelationId = RoomRelation['id']
export type RoomMediaId = RoomMedia['id']
